import React, { useEffect, useState, useMemo } from 'react';
import { Accordion, Button, Card, Stack, useAccordionButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import pluralize from 'pluralize';
import { useEffectOnce } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';
import { map } from 'lodash';

import { DownloadLinkButton } from '@components/Base';
import { Ingestion, IngestionTypes, tIngestionStatus, tIngestionType } from '@models/Ingestion';
import { formatDateTime } from '@utils/datetime';

import {
  ingestionsFetch,
  IngestionsFetchPayload,
  ingestionCreate,
  IngestionCreatePayload,
  stateClear,
} from './action';
import { selectFeatureIngestion } from './selector';
import { IngestionFormModal, IngestionFormValues } from './ingestion-form-modal';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = Pick<IngestionsFetchPayload, 'ingestableType' | 'ingestableId'> & {
  ingestionTypes: IngestionTypes[];
};

const IngestionCardBody = ({ ingestableType, ingestableId, ingestionTypes }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ingestions, isLoading, pagination } = useSelector(selectFeatureIngestion);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [show, setShow] = useState(false);
  const [selectedIngestionType, setSelectedIngestionType] = useState(ingestionTypes[0]);

  const columns = useMemo(() => [
    {
      name: t('common:type'),
      selector: ({ ingestionType }) => tIngestionType(ingestionType),
    },
    {
      name: t('common:createdDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      minWidth: '180px',
    },
    {
      name: t('common:creator'),
      selector: ({ creator }) => creator.name,
    },
    {
      name: t('common:status'),
      selector: ({ status }) => tIngestionStatus(status),
      minWidth: '180px',
    },
    {
      name: t('common:success'),
      selector: ({ successesCount }) => successesCount,
    },
    {
      name: t('common:fail'),
      selector: ({ errorsCount }) => errorsCount,
    },
    {
      name: t('common:skip'),
      selector: ({ skipsCount }) => skipsCount,
    },
    {
      name: t('common:data'),
      selector: ({ dataCsv }) => <DownloadLinkButton href={dataCsv} />,
    },
    {
      name: t('common:result'),
      selector: ({ resultCsv }) => <DownloadLinkButton href={resultCsv} />,
    },
  ] as TableColumn<Ingestion>[], []);

  useEffect(() => {
    const payload: IngestionsFetchPayload = { ingestableType, ingestableId, page, perPage };
    dispatch(ingestionsFetch(payload));
  }, [ingestableType, ingestableId, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              {pluralize(t('common:import'), 2)} ({pagination?.total})
            </Toggle>
            <Stack direction="horizontal" gap={3}>
              {map(ingestionTypes, (ingestionType) => (
                <Button
                  key={ingestableType}
                  onClick={() => {
                    setSelectedIngestionType(ingestionType);
                    setShow(true);
                  }}
                  variant="outline-primary"
                >
                  {tIngestionType(ingestionType)}
                </Button>
              ))}
            </Stack>
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <DataTable
              columns={columns}
              data={ingestions ?? []}
              customStyles={customStyles}
              fixedHeader
              pagination
              paginationServer
              progressPending={isLoading}
              paginationPerPage={perPage}
              paginationTotalRows={pagination?.total ?? 0}
              onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
              onChangePage={(newPage) => setPage(newPage)}
            />
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <IngestionFormModal
        show={show}
        title={tIngestionType(selectedIngestionType)}
        onHide={() => setShow(false)}
        onSubmit={(values: IngestionFormValues) => {
          const payload: IngestionCreatePayload = { ingestableType, ingestableId, ingestionType: selectedIngestionType, ...values };
          dispatch(ingestionCreate(payload));
          setShow(false);
        }}
      />
    </>
  );
};

export { IngestionCardBody };
